import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import Select from 'react-select';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import {
  listBookkeepingTemplates,
  getBookkeepingTemplate,
  suggestVerification,
  toUserErrorMessage,
} from '../../../../services/BookkeepingTemplateService';
import { classifyAssetText, inlineAssetCategoryOptions } from '../../Accounting/Assets/assetUtils';
import { resolveRoleAccounts } from '../../../../services/api/chartRolesApi';
import {
  FileText,
  DollarSign,
  Calendar,
  Hash,
  Edit2,
  Trash2,
  Plus,
  CheckCircle,
  AlertCircle,
  Briefcase,
  Lightbulb,
  List,
  X,
  Search
} from 'lucide-react';

/**
 * Modern verification modal with two modes:
 * 1. Template: Use predefined bookkeeping-templates with editable preview
 * 2. Manual: Create verification from scratch
 *
 * Template plumbing consumes the new bookkeeping-templates service
 * (BookkeepingTemplateService). The chart and payment-account fetches
 * still go through the existing /accounting/accounts endpoint.
 *
 * Suggestion response shape conversion happens at the boundary in
 * handleGeneratePreview — downstream consumers (the parent's
 * onConfirm → POST /approve|/pay) keep receiving the same internal
 * { account_id, account_number, account_name, debit, credit } rows.
 */

/**
 * Filter the chart for an `account_picker` input. The template body
 * filter syntax is `class:N` (one BAS class) or `class:N-M` (range).
 *
 * No filter (empty / null) → full chart, intentionally.
 * Malformed filter (anything that doesn't match the regex) → empty
 *   list. Returning the full chart on a typo'd filter silently leaks
 *   tax accounts into a cost-picker; an empty list forces the operator
 *   to notice the template is malformed.
 */
function filterAccountsForPicker(accounts, filter) {
  if (filter === undefined || filter === null || filter === '') return accounts;
  if (typeof filter !== 'string') return [];
  const m = filter.match(/^\s*class:(\d{1,2})(?:-(\d{1,2}))?\s*$/);
  if (!m) {
    console.warn('[VerificationModal] Unknown account_picker filter syntax:', filter);
    return [];
  }
  const lo = parseInt(m[1], 10);
  const hi = m[2] ? parseInt(m[2], 10) : lo;
  if (hi < lo) return [];
  return accounts.filter((a) => {
    // BAS class = first digit of the account number, valid for both
    // 4-digit huvudkonton (1930) and 5-digit underkonton (19301) per
    // BFNAR 2013:2 punkt 2.3. Integer-division on 19301 yielded klass=
    // 19 and silently dropped the account from the picker.
    const num = String(a.account_number ?? '');
    if (!/^\d{4,5}$/.test(num)) return false;
    const klass = parseInt(num[0], 10);
    return Number.isFinite(klass) && klass >= lo && klass <= hi;
  });
}

/**
 * Translate the evaluator's developer-grade English error strings into
 * Swedish user copy. Unknown messages pass through so support can still
 * read them on screenshots. Patterns mirror the strings emitted by
 * `accounting-backend/accountingtemplates/evaluator/evaluate.js`.
 */
function suggestErrorToSwedish(detail) {
  if (!detail || typeof detail !== 'string') return null;
  let m = detail.match(/Account ref ['"]?([^'"]+)['"]? did not resolve/);
  if (m) return `Fältet '${m[1]}' saknar ett kontonummer — välj ett konto i parameterformuläret.`;
  if (/Amount must be non-negative/.test(detail)) {
    return 'Beloppet blev negativt. Kontrollera momssats och inmatat belopp.';
  }
  if (/Amount expression did not evaluate to a finite number/.test(detail)) {
    return 'Beloppet kunde inte beräknas. Kontrollera att alla värden är ifyllda korrekt.';
  }
  if (/Debit\/credit imbalance/.test(detail)) {
    return 'Verifikationen balanserar inte — detta är ett mallfel. Kontakta support.';
  }
  if (/account_band lookup failed/.test(detail)) {
    return 'Saknat val i mallens kontorad — kontakta support.';
  }
  if (/Cannot auto-resolve side for account/.test(detail)) {
    return 'Mallfel — kontorad saknar debet/kredit-sida. Kontakta support.';
  }
  if (/expects an EU VAT number/.test(detail)) {
    return 'Felaktigt EU-momsnummer. Ange landskod + nummer, t.ex. DE123456789.';
  }
  return detail;
}
// Post-process for accrual invoice / year-end booking: swap a
// bank-account credit → 2440 leverantörsskulder so the fakturadag
// produces an A/P liability instead of an immediate cash outflow.
// Kept until fakturametoden-native invoice-flow templates exist
// (DEFERRED row 56).
//
// The swap is intentionally narrow:
//   - Only fires when verificationType is 'accrual_invoice' or
//     'year_end' (BFL 5 kap 7 § correctness path).
//   - Only the four canonical betalkonton (1910/1920/1930/1940).
//     Class-19 currency cash (1960 valutakassa, 1980) and other
//     1900-range accounts are NOT betalkonton and must NOT be
//     rewritten to 2440 — they participate in their own bokslut
//     flows (currency revaluation lives there per BFNAR 2016:10
//     14 kap).
//   - Only the credit side, since A/P liabilities live on the
//     credit side under faktureringsmetoden (BAS klass 2).
//
// Exported for unit testing (M10) — the helper is pure so the
// regression guard tests don't need to drive the full template
// flow through the modal UI.
export function applyAccrualInvoiceSwap(transactions, verificationType, allAccounts, apLeg = null) {
  if (verificationType !== 'accrual_invoice' && verificationType !== 'year_end') {
    return { transactions, swapInfo: null };
  }
  const SWAPPABLE_BETALKONTON = new Set(['1910', '1920', '1930', '1940']);
  // The A/P target comes from the tenant's accounts_payable chart role when
  // the caller resolved it; '2440' remains only the offline last resort so
  // the pure helper keeps working without network (M10 contract) — a chart
  // missing the target still surfaces via the missing-account UX.
  const apNumber = String((apLeg && apLeg.accountNumber) || '2440');
  const apAccount = (allAccounts || []).find(a => String(a.account_number) === apNumber);
  let swapInfo = null;
  const next = transactions.map(txn => {
    if (txn.credit && SWAPPABLE_BETALKONTON.has(String(txn.account_number))) {
      swapInfo = {
        from_number: txn.account_number,
        from_name: txn.account_name,
      };
      return {
        ...txn,
        account_id: apAccount ? apAccount.account_id : Number(apNumber),
        account_number: apNumber,
        account_name: apAccount ? apAccount.account_name
          : ((apLeg && apLeg.accountName) || 'Leverantörsskulder'),
      };
    }
    return txn;
  });
  return { transactions: next, swapInfo };
}



/**
 * Book-as-asset row derivation for the approval payload (exported for tests,
 * re-review RRR5/RR3). Swaps the cost debit to the category booking account;
 * for personbil (no input-VAT deduction — Skatteverket, Bilar och moms) the
 * 264x debits are removed and FOLDED INTO the capitalized asset debit, and
 * the register basis equals that VAT-inclusive debit.
 *
 * @returns {{ transactions: Array, blockedVat: number, acquisitionValue: number }}
 */
export function deriveAssetApproval(transactions, { swapIndex, assetAccountEntry, personbil }) {
  const swapRow = transactions[swapIndex];
  const blockedVat = personbil
    ? transactions
      .filter((t, i) => i !== swapIndex
        && /^264/.test(String(t.account_number || '')) && parseFloat(t.debit || 0) > 0)
      .reduce((sum, t) => sum + parseFloat(t.debit || 0), 0)
    : 0;
  const acquisitionValue = Math.round(((parseFloat(swapRow?.debit) || 0) + blockedVat) * 100) / 100;
  const derived = transactions
    .filter((t, i) => !(personbil && i !== swapIndex
      && /^264/.test(String(t.account_number || '')) && parseFloat(t.debit || 0) > 0))
    .map((t) => (t === swapRow
      ? {
          ...t,
          account_id: assetAccountEntry.account_id,
          account_number: assetAccountEntry.account_number,
          account_name: assetAccountEntry.account_name,
          debit: String(acquisitionValue),
        }
      : t));
  return { transactions: derived, blockedVat, acquisitionValue };
}

/**
 * Gate for manual-mode initialization (exported for tests — review P2).
 * Manual rows may only be initialized once BOTH the chart fetch and the
 * role fetch have settled (roleAccounts is null only while in flight):
 * initializing earlier would bake in a silent 19xx/2440 guess that no
 * rerun corrects. Settled-but-unresolved roles (null per-role values)
 * DO initialize — the structural fallback then applies, with the visible
 * unresolved-role warning, never silently.
 */
export function manualInitReady({ mode, allAccounts, roleAccounts, transactions }) {
  return mode === 'manual'
    && (allAccounts || []).length > 0
    && roleAccounts !== null
    && (transactions || []).length === 0;
}

const ASSET_YEARS_OPTIONS = ['3', '4', '5', '7', '10'];

const VerificationModal = ({
  show,
  onHide,
  companyId,
  invoice,
  invoiceId,
  onConfirm,
  isLoading,
  error,
  setError,
  verificationType,
  overrideAmount
}) => {
  // Mode state
  const [mode, setMode] = useState('template'); // 'template' or 'manual'

  // Common state
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);
  // Tenant chart roles: bank/A-P defaults come from the tenant's chart, not
  // pinned numbers. Unresolved roles degrade to the structural 19xx scan /
  // documented offline fallback rather than blocking the modal.
  const [roleAccounts, setRoleAccounts] = useState(null);
  useEffect(() => {
    if (!companyId) return undefined;
    let active = true;
    resolveRoleAccounts(companyId, ['primary_bank', 'accounts_payable']).then((legs) => {
      if (active) setRoleAccounts(legs);
    });
    return () => { active = false; };
  }, [companyId]);
  const findRoleAccount = (role) => {
    const resolved = roleAccounts && roleAccounts[role];
    if (!resolved) return null;
    return allAccounts.find(a => String(a.account_number) === String(resolved.accountNumber)) || null;
  };
  // Role fetch settled but a needed role is unresolved — the structural
  // fallback still lets the user work, but the state is SHOWN, never silent
  // (review P2): which roles matter depends on the verification type.
  const needsApRole = verificationType === 'accrual_invoice'
    || verificationType === 'accrual_payment' || verificationType === 'year_end';
  const unresolvedRoles = roleAccounts === null ? [] : [
    ...(!roleAccounts.primary_bank ? ['Företagskonto (primary_bank)'] : []),
    ...(needsApRole && !roleAccounts.accounts_payable ? ['Leverantörsskulder (accounts_payable)'] : []),
  ];
  const [selectedAccount, setSelectedAccount] = useState('');
  // When the 2440 swap fires under accrual_invoice / year_end, capture
  // the original account number so we can surface a banner explaining
  // the silent rewrite (BFL 5 kap 7 § "sambandet … utan svårighet ska
  // kunna fastställas"). Reset on every preview re-generate.
  const [swapInfo, setSwapInfo] = useState(null);

  // Fixed-asset interception (assets/detection/evaluate → capitalize).
  // Mirrors BookExpenseModal: when the proposed rows look like an
  // anläggningstillgång (net >= halvt prisbasbelopp, no 12xx/11xx account
  // touched), the modal defaults to booking it as a tillgång — the cost-
  // account debit row is swapped for the category's tillgångskonto and the
  // register asset is created atomically with the verifikat (asAsset).
  // Only on the accrual approve path (accrual_invoice) — the backend only
  // creates assets when the approval books a verifikat.
  const [assetSuggestion, setAssetSuggestion] = useState(null);
  const [assetChoice, setAssetChoice] = useState('asset'); // 'asset' | 'expense' (korttidsinventarie opt-out)
  const [assetCategoryCode, setAssetCategoryCode] = useState('computers');
  const [assetYears, setAssetYears] = useState('5');
  const [assetName, setAssetName] = useState('');
  const [assetVehicleType, setAssetVehicleType] = useState('personbil'); // vehicles only: personbil | lastbil
  // Tenant asset_categories rows (for asset_account numbers). Empty until
  // fetched — a suggestion triggers the fetch; ASSET_CATEGORIES + the seed
  // account map act as fallback if the endpoint fails.
  const [assetCategories, setAssetCategories] = useState([]);

  // Template mode state
  const [verificationTemplates, setVerificationTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedTemplateDetails, setSelectedTemplateDetails] = useState(null);
  const [templateParameters, setTemplateParameters] = useState([]);
  const [parameterValues, setParameterValues] = useState({});
  const [showPreview, setShowPreview] = useState(false);

  // Transaction state (shared between modes)
  const [transactions, setTransactions] = useState([]);
  const [totalDebit, setTotalDebit] = useState(0);
  const [totalCredit, setTotalCredit] = useState(0);

  // Fetch data when modal is shown
  useEffect(() => {
    if (show) {
      fetchPaymentAccounts();
      fetchAllAccounts();
      fetchVerificationTemplates();
      resetState();
    }
  }, [show]);

  // Calculate totals when transactions change
  useEffect(() => {
    calculateTotals();
  }, [transactions]);

  // Clear and initialize transactions when switching modes. Manual
  // initialization also waits for the role fetch to settle (roleAccounts is
  // null only while in flight) so the role default is used from the start —
  // never a silent structural fallback that no rerun corrects (review P2).
  useEffect(() => {
    if (mode === 'manual') {
      if (manualInitReady({ mode, allAccounts, roleAccounts, transactions: [] })) {
        initializeManualMode();
      } else {
        // Accounts or roles not loaded yet — clear; the effect below
        // initializes once both have settled.
        setTransactions([]);
      }
    } else if (mode === 'template') {
      // Switching to template mode - clear any manual transactions
      setTransactions([]);
      setShowPreview(false);
    }
  }, [mode]);

  // Set default selected account when accounts AND the role fetch are ready.
  // Waiting for roleAccounts to settle (it is null only while in flight)
  // prevents the race where the structural 19xx fallback wins and the
  // resolved primary_bank role never corrects it (review P2).
  useEffect(() => {
    if (allAccounts.length > 0 && roleAccounts !== null && !selectedAccount) {
      const roleBank = findRoleAccount('primary_bank');
      const firstBankAccount = allAccounts.find(a => {
        const accNum = parseInt(a.account_number);
        return accNum >= 1900 && accNum < 2000;
      });
      const defaultAcct = roleBank || firstBankAccount;
      if (defaultAcct) {
        setSelectedAccount(defaultAcct.account_id);
      }
    }
  }, [allAccounts, roleAccounts]);

  // Initialize manual mode transactions once accounts AND the role fetch
  // have settled (review P2 — must depend on roleAccounts so the resolved
  // role default is applied when it arrives, instead of a silent 19xx/2440
  // guess that nothing ever corrects).
  useEffect(() => {
    if (manualInitReady({ mode, allAccounts, roleAccounts, transactions })) {
      initializeManualMode();
    }
  }, [allAccounts, roleAccounts, mode]);

  // ─── Asset detection ───
  // Signature of what materially affects detection: netAmount (debit rows on
  // non-26xx accounts) + all account numbers. Computed from the ORIGINAL
  // (unswapped) rows in state — the tillgångskonto swap is derived at render
  // time precisely so it never feeds back into detection (a swapped 12xx row
  // would flip touchesAssetAccount and make the suggestion vanish).
  const detectionSignature = (() => {
    if (!show || verificationType !== 'accrual_invoice') return null;
    const rows = transactions.filter(
      (t) => t.account_number && (parseFloat(t.debit || 0) > 0 || parseFloat(t.credit || 0) > 0)
    );
    if (rows.length === 0) return null;
    const accountNumbers = rows.map((t) => String(t.account_number));
    const netAmount = rows
      .filter((t) => parseFloat(t.debit || 0) > 0 && !String(t.account_number).startsWith('26'))
      .reduce((s, t) => s + parseFloat(t.debit || 0), 0);
    if (netAmount <= 0) return null;
    return JSON.stringify({ netAmount: Math.round(netAmount * 100) / 100, accountNumbers });
  })();

  // Debounced evaluate whenever the rows change materially. The endpoint
  // persists a queue entry as a side effect (harmless — it acts as backstop
  // if the user books without the inline asset). Failures are non-fatal:
  // detection must never block approval.
  useEffect(() => {
    if (!detectionSignature) {
      setAssetSuggestion(null);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const { netAmount, accountNumbers } = JSON.parse(detectionSignature);
        const res = await axios.post(
          getApiUrl(`${companyId}/assets/detection/evaluate`),
          {
            sourceType: 'supplier_invoice',
            sourceId: invoiceId,
            date: invoice?.invoice_date,
            netAmount,
            accountNumbers,
            description: invoice?.supplier_name
              ? `Leverantörsfaktura ${invoice.invoice_number || ''} — ${invoice.supplier_name}`.trim()
              : undefined,
          },
          { withCredentials: true }
        );
        const sug = res.data?.suggestion || null;
        setAssetSuggestion(sug && sug.suggestion === 'capitalize' ? sug : null);
        if (sug?.suggestion === 'capitalize') {
          setAssetName((n) => n || sug.prefill?.name || invoice?.description || invoice?.supplier_name || '');
        }
      } catch (e) {
        // Non-fatal — no panel, approval unaffected.
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detectionSignature, companyId, invoiceId]);

  // AI classification prefill (A3) — once per invoice, only after the
  // deterministic detector said "capitalize" (AI refines, never decides).
  // Applies conservatively: only fields still at their defaults, so a user
  // who already picked a category/name is never overridden. Gated + credit-
  // metered server-side; classifyAssetText returns null on 402/403/errors
  // and the deterministic prefill simply stands.
  const classifiedInvoiceRef = useRef(null);
  useEffect(() => {
    if (!show || assetSuggestion?.suggestion !== 'capitalize') return undefined;
    if (classifiedInvoiceRef.current === invoiceId) return undefined;
    classifiedInvoiceRef.current = invoiceId;
    let cancelled = false;
    (async () => {
      const text = [
        invoice?.supplier_name && `Leverantör: ${invoice.supplier_name}`,
        invoice?.invoice_number && `Fakturanummer: ${invoice.invoice_number}`,
        invoice?.description && `Beskrivning: ${invoice.description}`,
        assetSuggestion?.prefill?.amount != null && `Belopp exkl. moms: ${assetSuggestion.prefill.amount} kr`,
      ].filter(Boolean).join('\n');
      const c = await classifyAssetText(companyId, text);
      if (cancelled || !c || (c.confidence || 0) < 0.5) return;
      setAssetCategoryCode((code) => (code === 'computers' ? c.category_code : code));
      if (c.useful_months) {
        setAssetYears((y) => (y === '5' ? String(Math.round(c.useful_months / 12)) : y));
      }
      if (c.name) {
        setAssetName((n) => {
          const deterministic = assetSuggestion?.prefill?.name || invoice?.description || invoice?.supplier_name || '';
          return !n || n === deterministic ? c.name : n;
        });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, assetSuggestion, invoiceId, companyId]);

  // Tenant asset categories — fetched lazily, only when a capitalize
  // suggestion is on screen (no extra request for ordinary invoices).
  // Failure is non-fatal: the panel falls back to ASSET_CATEGORIES + the
  // seed account map.
  useEffect(() => {
    if (!show || assetSuggestion?.suggestion !== 'capitalize') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(getApiUrl(`${companyId}/assets/categories`), { withCredentials: true });
        const data = res.data?.data ?? res.data;
        const rows = Array.isArray(data) ? data : data?.categories;
        if (!cancelled && Array.isArray(rows) && rows.length > 0) setAssetCategories(rows);
      } catch (e) { /* keep fallback */ }
    })();
    return () => { cancelled = true; };
  }, [show, companyId, assetSuggestion]);

  const resetState = () => {
    // accrual_payment starts in manual mode with pre-populated rows
    // All others start in template mode
    const initialMode = verificationType === 'accrual_payment' ? 'manual' : 'template';
    setMode(initialMode);
    setSelectedAccount(''); // Will be set from fetched accounts
    setSelectedTemplate('');
    setSelectedTemplateDetails(null);
    setTemplateParameters([]);
    setParameterValues({});
    setShowPreview(false);
    setTransactions([]);
    setAssetSuggestion(null);
    setAssetChoice('asset');
    setAssetCategoryCode('computers');
    setAssetYears('5');
    setAssetName('');
    setAssetVehicleType('personbil');
    setAssetCategories([]);
  };

  // Initialize manual mode with default transactions
  const initializeManualMode = () => {
    // The primary_bank chart role, or fall back to the structural bank range
    let bankAccount = findRoleAccount('primary_bank');

    if (!bankAccount) {
      bankAccount = allAccounts.find(a => {
        const accNum = parseInt(a.account_number);
        return accNum >= 1900 && accNum < 2000;
      });
    }

    const invoiceAmount = overrideAmount || invoice?.amount || 0;

    // For accrual_payment: pre-populate 2440 debit, bank credit
    if (verificationType === 'accrual_payment') {
      const apRole = roleAccounts && roleAccounts.accounts_payable;
      const apNumber = String((apRole && apRole.accountNumber) || '2440');
      const account2440 = allAccounts.find(a => String(a.account_number) === apNumber);
      setTransactions([
        {
          account_id: account2440 ? account2440.account_id : '',
          account_number: account2440 ? account2440.account_number : apNumber,
          account_name: account2440 ? account2440.account_name
            : ((apRole && apRole.accountName) || 'Leverantörsskulder'),
          debit: invoiceAmount,
          credit: ''
        },
        {
          account_id: bankAccount ? bankAccount.account_id : '',
          account_number: bankAccount ? bankAccount.account_number : '',
          account_name: bankAccount ? bankAccount.account_name : '',
          debit: '',
          credit: invoiceAmount
        }
      ]);
    } else {
      // Default manual mode: bank credit, empty debit row
      setTransactions([
        {
          account_id: bankAccount ? bankAccount.account_id : '',
          account_number: bankAccount ? bankAccount.account_number : '',
          account_name: bankAccount ? bankAccount.account_name : '',
          debit: '',
          credit: invoiceAmount
        },
        {
          account_id: '',
          account_number: '',
          account_name: '',
          debit: '',
          credit: ''
        }
      ]);
    }
  };

  // Fetch payment accounts
  const fetchPaymentAccounts = async () => {
    // Payment accounts will be filtered from all accounts (1900-1999 range)
    try {
      const accountsApiUrl = getApiUrl(`${companyId}/accounting/accounts?scope=company`);
      const response = await axios.get(accountsApiUrl, { withCredentials: true });
      const accounts = response.data.accounts || response.data || [];

      // Filter for bank accounts (typically 1900-1999 range)
      const bankAccounts = accounts.filter(acc => {
        const accNum = parseInt(acc.account_number);
        return accNum >= 1900 && accNum < 2000;
      });

      setPaymentAccounts(bankAccounts);
    } catch (error) {
      console.error('Error fetching payment accounts:', error);
      setError('Kunde inte hämta betalningskonton.');
    }
  };

  // Fetch all accounts for manual mode
  const fetchAllAccounts = async () => {
    try {
      const accountsApiUrl = getApiUrl(`${companyId}/accounting/accounts?scope=company`);
      const response = await axios.get(accountsApiUrl, { withCredentials: true });
      const accounts = response.data.accounts || response.data || [];

      // Map to ensure consistent structure
      const mappedAccounts = accounts.map(acc => ({
        account_id: acc.account_id || acc.account_number,
        account_number: acc.account_number,
        account_name: acc.account_name || acc.name || ''
      }));

      setAllAccounts(mappedAccounts);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setError('Kunde inte hämta kontoplanen.');
    }
  };

  // Fetch verification templates (new bookkeeping-templates surface)
  const fetchVerificationTemplates = async () => {
    try {
      const templates = await listBookkeepingTemplates(companyId);
      setVerificationTemplates(templates);
    } catch (error) {
      console.error('Error fetching verification templates:', error);
      setError(toUserErrorMessage(error));
    }
  };

  // Handle template change
  const handleTemplateChange = async (selectedOption) => {
    if (!selectedOption) {
      // Clear selection
      setSelectedTemplate('');
      setSelectedTemplateDetails(null);
      setTemplateParameters([]);
      setParameterValues({});
      setTransactions([]);
      setShowPreview(false);
      return;
    }

    const selectedTemplateId = selectedOption.value;
    setSelectedTemplate(selectedTemplateId);
    setTemplateParameters([]);
    setParameterValues({});
    setTransactions([]);
    setShowPreview(false);

    try {
      const template = await getBookkeepingTemplate(companyId, selectedTemplateId);
      setSelectedTemplateDetails(template);
      const inputs = (template.body && Array.isArray(template.body.inputs))
        ? template.body.inputs
        : [];
      setTemplateParameters(inputs);

      // Prefill values from the template body itself (select/account_picker defaults)
      // and from invoice data (known "amount" aliases when verificationType is set).
      // Invoice values win over template defaults if both apply to the same input.
      const defaultValues = {};
      inputs.forEach(input => {
        if ((input.type === 'select' || input.type === 'account_picker')
          && input.default !== undefined && input.default !== null && input.default !== '') {
          defaultValues[input.name] = String(input.default);
        }
      });

      const amountValues = {};
      if (verificationType && invoice) {
        inputs.forEach(input => {
          const name = input.name?.toLowerCase();
          if (['amount', 'total_amount', 'summa', 'belopp', 'totalbelopp'].includes(name)) {
            // Use overrideAmount (from partial payment dialog) if available, otherwise full invoice amount
            amountValues[input.name] = String(overrideAmount || invoice.amount || '');
          }
        });
      }

      const merged = { ...defaultValues, ...amountValues };
      if (Object.keys(merged).length > 0) {
        setParameterValues(prev => ({ ...prev, ...merged }));
      }
    } catch (error) {
      setError(toUserErrorMessage(error));
    }
  };

  // Clear template selection
  const handleClearTemplate = () => {
    setSelectedTemplate('');
    setSelectedTemplateDetails(null);
    setTemplateParameters([]);
    setParameterValues({});
    setTransactions([]);
    setShowPreview(false);
  };

  // Generate preview from template (new /suggest endpoint)
  const handleGeneratePreview = async () => {
    setError(null);
    setTransactions([]);
    setShowPreview(false);

    try {
      const inputs = { ...parameterValues };
      const result = await suggestVerification(companyId, selectedTemplate, inputs);

      // /suggest returns 422 with { ok:false, errors:[{path,error}] } on
      // validation or balance failure. The service swallows the 4xx so
      // we branch on result.ok rather than catch.
      if (!result || result.ok === false) {
        const detail = Array.isArray(result?.errors) && result.errors.length > 0
          ? result.errors[0].error
          : null;
        const swedish = suggestErrorToSwedish(detail);
        setError(swedish
          ? `Kunde inte generera förhandsgranskning: ${swedish}`
          : 'Kunde inte generera förhandsgranskning.');
        return;
      }

      // Convert new line shape → modal's internal transaction shape.
      // Side→debit/credit map mirrors NewTemplatePreviewStep.jsx so the
      // parent's onConfirm keeps receiving the same payload as before.
      //
      // Hard-fail if any line references an account the tenant chart
      // doesn't have. Falling back to `account_id: <4-digit string>`
      // would silently mis-book (backend parseInt picks the wrong row
      // in tenant-internal id-space) — see BFL 5 kap 7 §.
      const missingAccounts = [];
      const mapped = (result.lines || []).map(line => {
        const account = allAccounts.find(a => String(a.account_number) === String(line.account));
        if (!account) {
          missingAccounts.push(String(line.account));
          return null;
        }
        return {
          account_id: account.account_id,
          account_number: account.account_number,
          account_name: account.account_name,
          debit: line.side === 'debit' ? line.amount : '',
          credit: line.side === 'credit' ? line.amount : ''
        };
      });

      if (missingAccounts.length > 0) {
        const uniq = [...new Set(missingAccounts)];
        setError(
          `Kontoplanen saknar ${uniq.length === 1 ? 'kontot' : 'kontona'} ${uniq.join(', ')}. ` +
          'Importera saknade BAS-konton i kontoplanen innan du använder denna mall.'
        );
        return;
      }

      const { transactions: swapped, swapInfo: maybeSwap } = applyAccrualInvoiceSwap(
        mapped,
        verificationType,
        allAccounts,
        roleAccounts && roleAccounts.accounts_payable,
      );
      setSwapInfo(maybeSwap);
      setTransactions(swapped);
      setShowPreview(true);
    } catch (error) {
      setError(toUserErrorMessage(error));
    }
  };

  // Add new transaction row (manual mode)
  const handleAddTransaction = () => {
    // For the first transaction in manual mode, prefill the primary_bank role account
    const isFirstTransaction = transactions.length === 0;
    let defaultAccount = null;

    if (isFirstTransaction) {
      defaultAccount = findRoleAccount('primary_bank');
      if (!defaultAccount) {
        // Find first bank account in the 1900-1999 range
        defaultAccount = allAccounts.find(a => {
          const accNum = parseInt(a.account_number);
          return accNum >= 1900 && accNum < 2000;
        });
      }
    }

    setTransactions([
      ...transactions,
      {
        account_id: isFirstTransaction && defaultAccount ? defaultAccount.account_id : '',
        account_number: isFirstTransaction && defaultAccount ? defaultAccount.account_number : '',
        account_name: isFirstTransaction && defaultAccount ? defaultAccount.account_name : '',
        debit: '',
        credit: ''
      }
    ]);
  };

  // Remove transaction row
  const handleRemoveTransaction = (index) => {
    // In manual mode, always keep at least 2 rows
    if (mode === 'manual' && transactions.length <= 2) {
      return;
    }
    setTransactions(transactions.filter((_, i) => i !== index));
  };

  // Update transaction field
  const handleTransactionChange = (index, field, value) => {
    const updatedTransactions = [...transactions];

    if (field === 'account_id') {
      const account = allAccounts.find(a => a.account_id === parseInt(value));
      if (account) {
        updatedTransactions[index].account_id = account.account_id;
        updatedTransactions[index].account_number = account.account_number;
        updatedTransactions[index].account_name = account.account_name;
      }
    } else {
      updatedTransactions[index][field] = value;
    }

    setTransactions(updatedTransactions);
  };

  // Handle account selection change from react-select
  const handleAccountSelectChange = (index, selectedOption) => {
    const updatedTransactions = [...transactions];

    if (selectedOption) {
      updatedTransactions[index].account_id = selectedOption.value;
      updatedTransactions[index].account_number = selectedOption.account_number;
      updatedTransactions[index].account_name = selectedOption.account_name;
    } else {
      // Clear selection
      updatedTransactions[index].account_id = '';
      updatedTransactions[index].account_number = '';
      updatedTransactions[index].account_name = '';
    }

    setTransactions(updatedTransactions);
  };

  // Calculate totals
  const calculateTotals = () => {
    let debit = 0;
    let credit = 0;

    transactions.forEach((txn) => {
      debit += parseFloat(txn.debit || 0);
      credit += parseFloat(txn.credit || 0);
    });

    setTotalDebit(debit);
    setTotalCredit(credit);
  };

  // Check if verification is valid
  const isVerificationValid = () => {
    if (transactions.length === 0) return false;
    if (Math.abs(totalDebit - totalCredit) > 0.01) return false; // Allow small rounding differences

    // Check all transactions have required fields
    return transactions.every(txn =>
      txn.account_id &&
      (parseFloat(txn.debit || 0) > 0 || parseFloat(txn.credit || 0) > 0)
    );
  };

  // ─── Asset-panel derived state ───
  // Tenant rows win over the bundled fallback list. The debit row swaps to
  // the category's BOOKING account: on K1 pool classes that is the year's
  // acquisition account (12x1 — BFNAR 2006:1/BAS 2018 K1), never the base
  // account (review remediation F3). K1 property/land classes are excluded
  // from this inline flow — they need the tax classification and belong in
  // the full asset form.
  const assetCategoryList = inlineAssetCategoryOptions(assetCategories);
  const selectedAssetCategory =
    assetCategoryList.find((c) => c.code === assetCategoryCode) || assetCategoryList[0];
  // The chosen category's tillgångskonto resolved against the tenant chart —
  // required because the approve payload sends account_id (tenant-internal).
  const assetAccountEntry = selectedAssetCategory
    ? allAccounts.find((a) => String(a.account_number) === selectedAssetCategory.asset_account)
    : null;
  // The cost row is the (first) debit row that isn't moms (26xx), skuld
  // (24xx) or betalkonto (19xx).
  const assetSwapIndex = transactions.findIndex(
    (t) => parseFloat(t.debit || 0) > 0 && !/^(26|24|19)/.test(String(t.account_number || ''))
  );
  const assetPanelVisible =
    verificationType === 'accrual_invoice' && assetSuggestion?.suggestion === 'capitalize';
  const assetActive = assetPanelVisible && assetChoice === 'asset';
  const assetEngaged = assetActive && assetSwapIndex >= 0 && !!assetAccountEntry;
  const assetIsVehicle = ['vehicles', 'k1_vehicles'].includes(selectedAssetCategory?.code);
  const assetPersonbil = assetEngaged && assetIsVehicle && assetVehicleType === 'personbil';

  // Rows with the cost-account debit row swapped for the tillgångskonto.
  // Personbil (re-review RR3): no input-VAT deduction is allowed
  // (Skatteverket, Bilar och moms) — 264x debits are FOLDED INTO the
  // capitalized asset debit instead of being lifted; totals and balance
  // carry over unchanged. Derived, never written back to state: opting out
  // simply stops deriving, restoring the original rows. These rows are both
  // what the table shows and what onConfirm sends to /approve.
  const assetApproval = assetEngaged
    ? deriveAssetApproval(transactions, {
        swapIndex: assetSwapIndex,
        assetAccountEntry,
        personbil: assetPersonbil,
      })
    : null;
  const displayTransactions = assetApproval ? assetApproval.transactions : transactions;

  // Handle confirm
  const handleConfirmVerification = () => {
    // Book-as-asset: the swapped rows (tillgångskonto debit) go up with
    // asAsset, and the backend creates the register asset atomically with
    // the verifikat, returning assetIds.
    const asAsset = assetEngaged
      ? {
          category_code: selectedAssetCategory.code,
          // Personbil: the blocked VAT is part of the capitalized value
          // (folded into the asset debit above) — the register basis must
          // equal the posted debit (re-review RR3).
          acquisition_value: assetApproval.acquisitionValue,
          useful_months: (parseInt(assetYears, 10) || 5) * 12,
          name: (assetName || '').trim() || undefined,
          // The vehicle VAT fact is ALWAYS explicit (re-review RR3): the
          // category default must never be inherited silently.
          ...(assetIsVehicle && {
            vat_profile: assetVehicleType === 'lastbil' ? 'standard' : 'personbil',
          }),
        }
      : null;
    onConfirm({
      selectedAccount,
      selectedTemplate: mode === 'template' ? selectedTemplate : null,
      transactions: displayTransactions,
      parameterValues: mode === 'template' ? parameterValues : null,
      ...(asAsset && { asAsset })
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  return (
    <>
      <style>{`
        .type-selector {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .type-option {
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          padding: 1.5rem;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s ease;
          background: white;
        }

        .type-option:hover {
          border-color: #cbd5e1;
          background: #f8fafc;
        }

        .type-option.selected {
          border-color: var(--bs-primary);
          background: #eff7f1ff;
        }

        .type-icon {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 0.75rem;
          background: #f1f5f9;
          color: #64748b;
          transition: all 0.2s ease;
        }

        .type-option.selected .type-icon {
          background: var(--bs-primary);
          color: white;
        }

        .type-label {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.25rem;
          font-size: 1rem;
        }

        .type-description {
          font-size: 0.875rem;
          color: #64748b;
        }

        .close-button {
          position: absolute;
          top: 1.25rem;
          right: 1.5rem;
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          color: #64748b;
          width: 32px;
          height: 32px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          cursor: pointer;
        }

        .close-button:hover {
          background: #e2e8f0;
          color: #475569;
        }
      `}</style>

      <Modal show={show} onHide={onHide} size="lg">
        <Modal.Header style={styles.header}>
          <button type="button" className="close-button" onClick={onHide}>
            <X size={16} />
          </button>
          <Modal.Title style={styles.title}>
            <FileText size={24} style={{ marginRight: '12px', color: '#3b82f6' }} />
            {verificationType === 'accrual_invoice' ? 'Bokför faktura (leverantörsskuld)' :
             verificationType === 'accrual_payment' ? 'Bokför betalning' :
             verificationType === 'cash_payment' ? 'Bokför betalning' :
             verificationType === 'year_end' ? 'Årsbokslut — Bokför som leverantörsskuld' :
             'Skapa verifikation'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={styles.body}>
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible style={styles.alert}>
              <AlertCircle size={18} style={{ marginRight: '8px' }} />
              {error}
            </Alert>
          )}

          {/* A needed chart role is unresolved — the fallback default still
              works, but the state is shown, never silent (review P2). */}
          {unresolvedRoles.length > 0 && (
            <Alert variant="warning" style={styles.alert}>
              <AlertCircle size={18} style={{ marginRight: '8px' }} />
              {unresolvedRoles.length === 1
                ? `Rollen ${unresolvedRoles[0]} är inte mappad eller kunde inte hämtas — standardkontot gissas från kontoplanen. `
                : `Rollerna ${unresolvedRoles.join(' och ')} är inte mappade eller kunde inte hämtas — standardkonton gissas från kontoplanen. `}
              <a href={`/${companyId}/settings/chart?vy=roller`}>Kontrollera kontoplansrollerna</a>.
            </Alert>
          )}

          {/* Informational banner based on verification type */}
          {verificationType === 'accrual_invoice' && (
            <Alert variant="info" style={{ ...styles.alert, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af' }}>
              <Briefcase size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
              <span><strong>Faktureringsmetoden:</strong> Fakturan bokförs som leverantörsskuld (konto 2440). Betalningen bokförs separat.</span>
            </Alert>
          )}
          {verificationType === 'accrual_payment' && (
            <Alert variant="info" style={{ ...styles.alert, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af' }}>
              <Briefcase size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
              <span><strong>Bokför betalningen</strong> — reglerar leverantörsskulden (konto 2440).</span>
            </Alert>
          )}
          {verificationType === 'cash_payment' && (
            <Alert variant="info" style={{ ...styles.alert, background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534' }}>
              <Briefcase size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
              <span><strong>Kontantmetoden:</strong> Kostnaden och momsen bokförs vid betalning.</span>
            </Alert>
          )}
          {verificationType === 'year_end' && (
            <Alert variant="info" style={{ ...styles.alert, background: '#faf5ff', border: '1px solid #e9d5ff', color: '#6b21a8' }}>
              <Briefcase size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
              <span><strong>Kontantmetoden årsbokslut:</strong> Fakturan bokförs som leverantörsskuld (konto 2440).</span>
            </Alert>
          )}

          {/* Mode Selection Cards — hidden for accrual_payment (always manual) */}
          {verificationType !== 'accrual_payment' && (
          <div style={styles.section}>
            <h5 style={styles.sectionTitle}>
              <Briefcase size={20} />
              Välj metod
            </h5>

            <div className="type-selector">
              <div
                className={`type-option ${mode === 'template' ? 'selected' : ''}`}
                onClick={() => setMode('template')}
              >
                <div className="type-icon">
                  <List size={24} />
                </div>
                <div className="type-label">Mall</div>
                <div className="type-description">Använd färdiga mallar med parametrar</div>
              </div>

              <div
                className={`type-option ${mode === 'manual' ? 'selected' : ''}`}
                onClick={() => setMode('manual')}
              >
                <div className="type-icon">
                  <Edit2 size={24} />
                </div>
                <div className="type-label">Manuell</div>
                <div className="type-description">Skapa verifikation från grunden</div>
              </div>
            </div>
          </div>
          )}

          {/* Template Mode */}
          {mode === 'template' && (
              <div style={styles.section}>
                <h5 style={styles.sectionTitle}>
                  <Briefcase size={20} />
                  Mall
                </h5>

                {/* Template Selection */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>Sök och välj mall *</label>
                  <Select
                    options={verificationTemplates.map(t => ({
                      value: t.template_id,
                      label: t.template_name,
                      description: t.description,
                      type: t.template_type
                    }))}
                    value={selectedTemplate ?
                      verificationTemplates.map(t => ({
                        value: t.template_id,
                        label: t.template_name,
                        description: t.description,
                        type: t.template_type
                      })).find(opt => opt.value === selectedTemplate) : null
                    }
                    onChange={handleTemplateChange}
                    placeholder="Skriv för att söka mallar..."
                    isClearable
                    isSearchable
                    noOptionsMessage={() => "Inga mallar hittades"}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        padding: '0.25rem',
                        fontSize: '14px'
                      }),
                      menuPortal: (provided) => ({
                        ...provided,
                        zIndex: 9999
                      }),
                      menu: (provided) => ({
                        ...provided,
                        fontSize: '14px'
                      })
                    }}
                  />
                </div>

                {/* Template Details Card */}
                {selectedTemplateDetails && (
                  <div style={styles.templateDetailsCard}>
                    <div style={styles.templateDetailsHeader}>
                      <div>
                        <h6 style={styles.templateDetailsTitle}>{selectedTemplateDetails.template_name}</h6>
                        {selectedTemplateDetails.description && (
                          <p style={styles.templateDetailsDescription}>{selectedTemplateDetails.description}</p>
                        )}
                      </div>
                      <Button
                        variant="link"
                        onClick={handleClearTemplate}
                        style={styles.clearTemplateButton}
                      >
                        <X size={16} style={{ marginRight: '4px' }} />
                        Byt mall
                      </Button>
                    </div>
                    {selectedTemplateDetails.template_type && (
                      <div style={styles.templateTypeBadge}>
                        {selectedTemplateDetails.template_type}
                      </div>
                    )}
                  </div>
                )}

                {/* Template Parameters (new body.inputs shape) */}
                {templateParameters.length > 0 && (
                  <div style={styles.section}>
                    <h5 style={styles.sectionTitle}>
                      <Hash size={20} />
                      Parametrar
                    </h5>
                    {allAccounts.length === 0 && (
                      <Alert variant="info" style={styles.alert}>
                        Laddar kontoplan — vänta tills listan är klar innan du fyller i kontoval.
                      </Alert>
                    )}
                    <div style={styles.parametersGrid}>
                      {templateParameters.map((input, index) => {
                        // Under faktureringsmetoden (accrual_invoice) and
                        // kontantmetoden-årsbokslut (year_end), the
                        // motkonto picker is meaningless — the credit
                        // side always becomes 2440 leverantörsskuld
                        // (handled by the swap in handleGeneratePreview).
                        // The seed's default ('1930') still flows so
                        // /suggest balances; we just don't render a
                        // confusing picker. The actual betalkonto is
                        // chosen later at the betalningssteg.
                        // DEFERRED row 65 (c).
                        const swapWillRewrite =
                          (verificationType === 'accrual_invoice' || verificationType === 'year_end')
                          && input.name === 'motkonto';
                        if (swapWillRewrite) return null;

                        const isNumeric = ['currency', 'currency_gross', 'currency_net', 'number', 'integer'].includes(input.type);
                        const isDate = input.type === 'date';
                        const isSelect = input.type === 'select';
                        const isAccountPicker = input.type === 'account_picker';
                        const labelText = (input.label || input.name) + (input.required !== false ? ' *' : '');

                        // Dropdown types: `select` uses the template's bundled options;
                        // `account_picker` uses the chart filtered by `class:N` or `class:N-M`.
                        // The value sent to /suggest is the 4-digit account_number string
                        // (matches the evaluator's account_ref validator).
                        if (isSelect || isAccountPicker) {
                          const options = isSelect
                            ? (Array.isArray(input.options) ? input.options : []).map(o => {
                                // If the option value is a 4-digit account number,
                                // pull the canonical "<number> - <name>" from the
                                // tenant chart. Fall back to the bundled label for
                                // non-account selects (e.g. momssats integers) or
                                // when the chart has no row for the value.
                                const val = String(o.value);
                                const account = /^\d{4}$/.test(val)
                                  ? allAccounts.find(a => String(a.account_number) === val)
                                  : null;
                                const label = account
                                  ? `${account.account_number} - ${account.account_name}`
                                  : (o.label || val);
                                return { value: val, label };
                              })
                            : filterAccountsForPicker(allAccounts, input.filter).map(a => ({
                                value: String(a.account_number),
                                label: `${a.account_number} - ${a.account_name}`,
                              }));
                          const currentValue = parameterValues[input.name];
                          const selectedOption = (currentValue !== undefined && currentValue !== '')
                            ? options.find(opt => opt.value === String(currentValue)) || null
                            : null;
                          return (
                            <div key={index} style={styles.formGroup}>
                              <label style={styles.label}>{labelText}</label>
                              <Select
                                options={options}
                                value={selectedOption}
                                onChange={(opt) =>
                                  setParameterValues({
                                    ...parameterValues,
                                    [input.name]: opt ? opt.value : '',
                                  })
                                }
                                placeholder={isAccountPicker ? 'Sök konto...' : 'Välj...'}
                                isClearable
                                isSearchable
                                noOptionsMessage={() => isAccountPicker ? 'Inget konto hittades' : 'Inga val'}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{
                                  control: (provided) => ({
                                    ...provided,
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    padding: '0.1rem',
                                    fontSize: '14px',
                                    minHeight: '42px',
                                  }),
                                  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
                                  menu: (provided) => ({ ...provided, fontSize: '14px' }),
                                }}
                              />
                            </div>
                          );
                        }

                        const isEuVat = input.type === 'eu_vat_number';
                        const htmlType = isNumeric ? 'number' : isDate ? 'date' : 'text';
                        return (
                          <div key={index} style={styles.formGroup}>
                            <label style={styles.label}>{labelText}</label>
                            <div style={styles.inputWrapper}>
                              {isNumeric ? (
                                <DollarSign size={18} style={styles.inputIcon} />
                              ) : isDate ? (
                                <Calendar size={18} style={styles.inputIcon} />
                              ) : (
                                <FileText size={18} style={styles.inputIcon} />
                              )}
                              <Form.Control
                                type={htmlType}
                                value={parameterValues[input.name] || ''}
                                onChange={(e) =>
                                  setParameterValues({
                                    ...parameterValues,
                                    [input.name]: e.target.value,
                                  })
                                }
                                style={styles.input}
                                step={isNumeric ? '0.01' : undefined}
                                placeholder={isEuVat ? 't.ex. DE123456789' : undefined}
                                autoCapitalize={isEuVat ? 'characters' : undefined}
                                spellCheck={isEuVat ? false : undefined}
                              />
                            </div>
                            {isEuVat && (
                              <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                EU-momsnummer från annat EU-land. Svenska (SE) nummer accepteras inte — använd en inhemsk inköpsmall istället.
                              </small>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <Button
                      variant="primary"
                      onClick={handleGeneratePreview}
                      disabled={
                        !selectedTemplate
                        || allAccounts.length === 0
                        || templateParameters.some(p => p.required !== false && !parameterValues[p.name])
                      }
                      style={styles.generateButton}
                    >
                      <CheckCircle size={18} style={{ marginRight: '8px' }} />
                      Generera förhandsgranskning
                    </Button>
                  </div>
                )}
              </div>
          )}

          {/* Manual Mode */}
          {mode === 'manual' && (
            <div style={styles.section}>
              <h5 style={styles.sectionTitle}>
                <Edit2 size={20} />
                Transaktioner
              </h5>
            </div>
          )}

        {/* Transactions Table (shown in both modes when there are transactions) */}
        {transactions.length > 0 && (
          <div style={styles.section}>
            <h5 style={styles.sectionTitle}>
              <FileText size={20} />
              Transaktioner
              {mode === 'template' && <span style={styles.editableHint}>(Klicka för att redigera)</span>}
            </h5>

            {swapInfo && (
              <Alert
                variant="info"
                style={{ ...styles.alert, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af' }}
              >
                <Briefcase size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
                <span>
                  <strong>Motkonto bytt till 2440 leverantörsskuld.</strong>{' '}
                  Mallens kredit på {swapInfo.from_number} {swapInfo.from_name} ersattes enligt faktureringsmetoden — fakturan blir en leverantörsskuld nu, betalningen bokförs separat senare.
                </span>
              </Alert>
            )}

            {/* ─── Fixed-asset interception (assetSuggestion: capitalize) ───
                Accrual approve path only. The opt-out wording is deliberate:
                above the halvt-prisbasbelopp threshold, direct expensing is
                only lawful for korttidsinventarier (ekonomisk livslängd
                ≤ 3 år, IL 18 kap. 4 §). */}
            {assetPanelVisible && (
              <div
                style={{
                  background: 'var(--rgba-primary-1)',
                  borderRadius: 8,
                  padding: '0.875rem 1rem',
                  marginBottom: 16,
                }}
              >
                <div className="d-flex align-items-start gap-2">
                  <div
                    style={{
                      flexShrink: 0, width: 30, height: 30, borderRadius: 9,
                      background: '#fff', color: 'var(--primary)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <Lightbulb size={16} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: 'var(--title)', fontSize: '0.9375rem' }}>
                      Det här ser ut som en tillgång
                    </div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--secondary)', marginTop: 2 }}>
                      {assetSuggestion.reason}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 10, fontSize: 13 }}>
                  <Form.Check
                    type="radio" id="si-asset-choice-asset"
                    label="Bokför som tillgång (rekommenderas)"
                    name="si-asset-choice" checked={assetChoice === 'asset'}
                    onChange={() => setAssetChoice('asset')}
                    disabled={isLoading}
                  />
                  <Form.Check
                    type="radio" id="si-asset-choice-expense"
                    label="Nej — den håller högst 3 år (korttidsinventarie, dras av direkt)"
                    name="si-asset-choice" checked={assetChoice === 'expense'}
                    onChange={() => setAssetChoice('expense')}
                    disabled={isLoading}
                  />
                </div>

                {assetChoice === 'asset' && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1.3fr', gap: 8, marginTop: 10 }}>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 3 }}>
                          Tillgångskategori
                        </div>
                        <Select
                          options={assetCategoryList.map((c) => ({ value: c.code, label: c.label }))}
                          value={selectedAssetCategory
                            ? { value: selectedAssetCategory.code, label: selectedAssetCategory.label }
                            : null}
                          onChange={(opt) => setAssetCategoryCode(opt ? opt.value : 'computers')}
                          isClearable={false}
                          isSearchable={false}
                          isDisabled={isLoading}
                          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            control: (base) => ({ ...base, minHeight: 31, fontSize: '0.8125rem', borderColor: '#e2e8f0' }),
                            option: (base) => ({ ...base, fontSize: '0.8125rem' }),
                            singleValue: (base) => ({ ...base, fontSize: '0.8125rem' }),
                          }}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 3 }}>
                          Nyttjandeperiod
                        </div>
                        <Form.Select
                          size="sm" value={assetYears}
                          onChange={(e) => setAssetYears(e.target.value)}
                          disabled={isLoading}
                        >
                          {ASSET_YEARS_OPTIONS.map((y) => (
                            <option key={y} value={y}>{y} år</option>
                          ))}
                        </Form.Select>
                        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>
                          5 år är standard enligt K2
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 3 }}>
                          Benämning
                        </div>
                        <Form.Control
                          size="sm" type="text" maxLength={255}
                          value={assetName}
                          onChange={(e) => setAssetName(e.target.value)}
                          placeholder="T.ex. MacBook Pro 14”"
                          disabled={isLoading}
                        />
                      </div>
                    </div>

                    {/* Fordon: personbil vs lastbil styr vat_profile (mirrors
                        CreateAssetForm) — lastbil/arbetsfordon fick dra av
                        momsen, så försäljningen bokförs med moms. */}
                    {['vehicles', 'k1_vehicles'].includes(selectedAssetCategory?.code) && (
                      <div style={{ marginTop: 10, fontSize: 13 }}>
                        <Form.Check
                          type="radio"
                          id="si-asset-vehicle-personbil"
                          name="si-asset-vehicle-type"
                          label="Personbil — momsen fick inte dras av vid köpet, försäljning blir momsfri"
                          checked={assetVehicleType === 'personbil'}
                          onChange={() => setAssetVehicleType('personbil')}
                          disabled={isLoading}
                        />
                        <Form.Check
                          type="radio"
                          id="si-asset-vehicle-lastbil"
                          name="si-asset-vehicle-type"
                          label="Lastbil eller arbetsfordon — momsen drogs av, försäljning bokförs med moms"
                          checked={assetVehicleType === 'lastbil'}
                          onChange={() => setAssetVehicleType('lastbil')}
                          disabled={isLoading}
                        />
                      </div>
                    )}

                    {/* The chart lacks the category's tillgångskonto — the
                        swap (and asAsset) can't apply until it's imported. */}
                    {assetActive && !assetAccountEntry && allAccounts.length > 0 && (
                      <div style={{ fontSize: 12, color: '#b45309', marginTop: 10 }}>
                        <AlertCircle size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
                        Kontoplanen saknar konto {selectedAssetCategory?.asset_account} — importera det
                        i kontoplanen (eller välj en annan kategori) för att kunna bokföra som tillgång.
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Konto</th>
                    <th style={styles.th}>Debet</th>
                    <th style={styles.th}>Kredit</th>
                    <th style={styles.th}>Åtgärd</th>
                  </tr>
                </thead>
                <tbody>
                  {displayTransactions.map((txn, index) => (
                    <tr key={index} style={styles.tr}>
                      <td style={styles.td}>
                        <Select
                          options={allAccounts.map(acc => ({
                            value: acc.account_id,
                            label: `${acc.account_number} - ${acc.account_name}`,
                            account_number: acc.account_number,
                            account_name: acc.account_name
                          }))}
                          value={txn.account_id ?
                            {
                              value: txn.account_id,
                              label: `${txn.account_number} - ${txn.account_name}`,
                              account_number: txn.account_number,
                              account_name: txn.account_name
                            } : null
                          }
                          onChange={(selectedOption) => handleAccountSelectChange(index, selectedOption)}
                          placeholder="Sök konto..."
                          isClearable
                          isSearchable
                          // The swapped tillgångskonto row is derived — a
                          // manual account edit here would be clobbered on
                          // next render. Lock it; opt out (or change the
                          // category) to control the account instead.
                          isDisabled={assetEngaged && index === assetSwapIndex}
                          noOptionsMessage={() => "Inget konto hittades"}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              minHeight: '36px',
                              border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                              fontSize: '13px'
                            }),
                            menuPortal: (provided) => ({
                              ...provided,
                              zIndex: 9999
                            }),
                            menu: (provided) => ({
                              ...provided,
                              fontSize: '13px'
                            }),
                            option: (provided) => ({
                              ...provided,
                              fontSize: '13px'
                            })
                          }}
                        />
                      </td>
                      <td style={styles.td}>
                        <Form.Control
                          type="number"
                          value={txn.debit || ''}
                          onChange={(e) => handleTransactionChange(index, 'debit', e.target.value)}
                          style={styles.tableInput}
                          step="0.01"
                          placeholder="0.00"
                        />
                      </td>
                      <td style={styles.td}>
                        <Form.Control
                          type="number"
                          value={txn.credit || ''}
                          onChange={(e) => handleTransactionChange(index, 'credit', e.target.value)}
                          style={styles.tableInput}
                          step="0.01"
                          placeholder="0.00"
                        />
                      </td>
                      <td style={styles.td}>
                        <Button
                          variant="link"
                          onClick={() => handleRemoveTransaction(index)}
                          style={{
                            ...styles.removeButton,
                            opacity: mode === 'manual' && transactions.length <= 2 ? 0.3 : 1,
                            cursor: mode === 'manual' && transactions.length <= 2 ? 'not-allowed' : 'pointer'
                          }}
                          title={mode === 'manual' && transactions.length <= 2 ? 'Minst 2 rader krävs' : 'Ta bort rad'}
                          disabled={mode === 'manual' && transactions.length <= 2}
                        >
                          <Trash2 size={18} color="#ef4444" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  <tr style={styles.totalRow}>
                    <td style={{ ...styles.td, fontWeight: '600' }}>Totalt</td>
                    <td style={{ ...styles.td, fontWeight: '600', color: '#3b82f6' }}>
                      {formatCurrency(totalDebit)}
                    </td>
                    <td style={{ ...styles.td, fontWeight: '600', color: '#3b82f6' }}>
                      {formatCurrency(totalCredit)}
                    </td>
                    <td style={styles.td}></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Balance Check */}
            <Alert
              variant={Math.abs(totalDebit - totalCredit) < 0.01 ? 'success' : 'warning'}
              style={styles.balanceAlert}
            >
              {Math.abs(totalDebit - totalCredit) < 0.01 ? (
                <>
                  <CheckCircle size={18} style={{ marginRight: '8px' }} />
                  Verifikationen är balanserad
                </>
              ) : (
                <>
                  <AlertCircle size={18} style={{ marginRight: '8px' }} />
                  Differens: {formatCurrency(Math.abs(totalDebit - totalCredit))}
                  {totalDebit > totalCredit ? ' (Debet > Kredit)' : ' (Kredit > Debet)'}
                </>
              )}
            </Alert>

            {mode === 'manual' && (
              <Button
                variant="outline-primary"
                onClick={handleAddTransaction}
                style={styles.addButton}
              >
                <Plus size={18} style={{ marginRight: '8px' }} />
                Lägg till rad
              </Button>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer style={styles.footer}>
        <Button variant="light" onClick={onHide} style={styles.cancelButton}>
          Avbryt
        </Button>
        <Button
          variant="success"
          onClick={handleConfirmVerification}
          disabled={!isVerificationValid() || isLoading}
          style={styles.confirmButton}
        >
          {isLoading ? (
            'Skapar...'
          ) : (
            <>
              <CheckCircle size={18} style={{ marginRight: '8px' }} />
              Bekräfta och skapa verifikation
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

// Modern styles
const styles = {
  header: {
    borderBottom: '1px solid #e5e7eb',
    padding: '20px 24px',
    position: 'relative',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '20px',
    fontWeight: '600',
    color: '#1f2937',
    margin: 0,
  },
  body: {
    padding: '24px',
    maxHeight: '70vh',
    overflowY: 'auto',
  },
  footer: {
    borderTop: '1px solid #e5e7eb',
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
  },
  alert: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
    borderRadius: '8px',
  },
  section: {
    marginBottom: '24px',
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: '16px',
    paddingBottom: '8px',
    borderBottom: '1px solid #f3f4f6',
  },
  editableHint: {
    fontSize: '13px',
    fontWeight: '400',
    color: '#6b7280',
    marginLeft: '8px',
    fontStyle: 'italic',
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '6px',
    display: 'block',
  },
  inputWrapper: {
    position: 'relative',
  },
  inputIcon: {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#9ca3af',
    zIndex: 1,
  },
  input: {
    paddingLeft: '40px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    transition: 'all 0.2s ease',
    height: '42px',
  },
  parametersGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
    marginBottom: '20px',
  },
  generateButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 20px',
    borderRadius: '8px',
    fontWeight: '500',
    fontSize: '14px',
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 20px',
    borderRadius: '8px',
    fontWeight: '500',
    fontSize: '14px',
    marginTop: '12px',
  },
  tableWrapper: {
    overflowX: 'auto',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    marginBottom: '16px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  th: {
    padding: '12px',
    textAlign: 'left',
    backgroundColor: '#f9fafb',
    fontWeight: '600',
    color: '#374151',
    borderBottom: '2px solid #e5e7eb',
  },
  tr: {
    borderBottom: '1px solid #f3f4f6',
  },
  td: {
    padding: '8px 12px',
    verticalAlign: 'middle',
  },
  totalRow: {
    backgroundColor: '#f9fafb',
    borderTop: '2px solid #e5e7eb',
  },
  tableInput: {
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '6px 10px',
    fontSize: '13px',
    width: '100%',
  },
  removeButton: {
    padding: '4px',
    border: 'none',
    background: 'transparent',
  },
  balanceAlert: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '12px',
  },
  cancelButton: {
    padding: '10px 20px',
    borderRadius: '8px',
    fontWeight: '500',
    fontSize: '14px',
    border: '1px solid #d1d5db',
  },
  confirmButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 20px',
    borderRadius: '8px',
    fontWeight: '500',
    fontSize: '14px',
  },
  templateDetailsCard: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '16px',
    marginTop: '16px',
    marginBottom: '16px',
  },
  templateDetailsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '8px',
  },
  templateDetailsTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
    margin: '0 0 4px 0',
  },
  templateDetailsDescription: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0,
  },
  clearTemplateButton: {
    padding: '4px 12px',
    fontSize: '13px',
    color: '#64748b',
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
  },
  templateTypeBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    background: '#eff7f1ff',
    color: 'var(--bs-primary)',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500',
    marginTop: '8px',
  },
};

export default VerificationModal;
